import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { cmdbBaselineCompareQuery, type BaselineComparePage as ComparePage, type Pair } from '../api/cmdb'
import { ApiError } from '../api/client'
import { moduleOf, shellQuery } from '../api/shell'
import { Forbidden } from '../ui/Forbidden'
import { Content } from '../ui/Shell'
import { Spinner } from '../ui/Spinner'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * `Cmdb::compareBaseline` - ce e altfel acum, la /app/cmdb/compareBaseline/{id}.
 *
 * Cinci liste, in ordinea in care conteaza dupa un incident:
 *   1. ce a disparut - cel mai des explica "marti mergea";
 *   2. ce s-a schimbat;
 *   3. legaturile disparute si cele noi - graful se muta mai des decat pare;
 *   4. ce a aparut.
 */

const STYLES = ['css/adm.css']

export function CmdbBaselineComparePage({ id }: { id: number }) {
  const shell = useQuery(shellQuery(moduleOf('/cmdb')))
  usePageStyles(STYLES, shell.data?.version ?? '')
  const page = useQuery(cmdbBaselineCompareQuery(id))

  if (page.error instanceof ApiError && page.error.status === 403) return <Forbidden />
  if (page.error instanceof ApiError && page.error.status === 404) {
    return (
      <Content>
        <div className="alert alert-warning" style={{ margin: 20 }}>
          <i className="fas fa-exclamation-circle"></i> No such snapshot, or it belongs to another customer.
        </div>
      </Content>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <Content>
      <Compare page={page.data} />
    </Content>
  )
}

function Compare({ page }: { page: ComparePage }) {
  const { baseline, diff } = page
  const total =
    diff.gone.length + diff.changed.length + diff.appeared.length + diff.edges_gone.length + diff.edges_new.length

  return (
    <div className="row">
      <div className="col-md-12">
        <div className="adm-panel" style={{ marginBottom: 16 }}>
          <p style={{ margin: 0 }}>
            <strong>{baseline.name}</strong>, taken {baseline.takenAt}{' '}
            {baseline.scopeName !== null && baseline.scopeName !== '' ? (
              <>
                over <strong>{baseline.scopeName}</strong> and everything under it
              </>
            ) : (
              <>over everything we hold</>
            )}{' '}
            &mdash; {baseline.ciCount} items, {baseline.edgeCount} links.
          </p>
        </div>

        {total === 0 ? (
          <div className="adm-panel">
            <p style={{ margin: 0 }}>Nothing has changed since.</p>
            <p className="text-muted small" style={{ margin: '6px 0 0' }}>
              {/*
               * "Nimic" e o afirmatie tare, deci se spune exact ce s-a comparat.
               * Altfel cineva ar putea crede ca s-au comparat si lucruri care
               * nu intrau in fotografie.
               */}
              Compared: the name, the state, the criticality, the environment, both owners, and the links between the
              items in the snapshot. Class field values are compared as a fingerprint &mdash; see{' '}
              <a href="/app/adm/changes">Changes</a> for what exactly changed inside them.
            </p>
          </div>
        ) : (
          <>
            {diff.truncated && (
              <p className="text-warning">
                <i className="fas fa-exclamation-triangle"></i> At least one of the lists was cut off at {diff.limit}{' '}
                rows. This comparison is <strong>partial</strong>.
              </p>
            )}

            <Items
              rows={diff.gone}
              title="Gone"
              tone="#a0362a"
              icon="fa-minus-circle"
              note="were in the snapshot; they no longer exist, or were retired"
            />

            {diff.changed.length > 0 && (
              <Card title="Changed" tone="#c2571f" icon="fa-pen" note="then &rarr; now" count={diff.changed.length}>
                {diff.changed.map((row, index) => (
                  <tr key={index}>
                    <td style={{ width: '38%' }}>
                      <a href={`/app/cmdb/view/${Number(row.ci_id)}`} target="_blank" rel="noopener">
                        {String(row.new_name ?? '')}
                      </a>
                    </td>
                    <td>
                      {changesOf(row, page.environments).map((change, i) => (
                        <div key={i}>
                          <span className="adm-muted">{change[0]}:</span> {change[1]}{' '}
                          <i className="fas fa-arrow-right adm-muted"></i> <strong>{change[2]}</strong>
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
              </Card>
            )}

            <Edges
              rows={diff.edges_gone}
              title="Links that are gone"
              tone="#a0362a"
              icon="fa-unlink"
              note="the names are the ones from the snapshot"
            />
            <Edges
              rows={diff.edges_new}
              title="New links"
              tone="#3c7d4a"
              icon="fa-link"
              note="between items that were both in the snapshot"
            />
            <Items
              rows={diff.appeared}
              title="Appeared"
              tone="#3c7d4a"
              icon="fa-plus-circle"
              note="were not in the snapshot, are here now, within the same scope"
            />
          </>
        )}

        <p>
          <a className="btn btn-default btn-sm" href="/app/cmdb/baselines">
            <i className="fas fa-arrow-left"></i> All snapshots
          </a>
        </p>
      </div>
    </div>
  )
}

/**
 * Numai campurile care CHIAR difera.
 *
 * Un rand care ar arata toate cele sase, cu cinci identice, ascunde exact
 * bucata pentru care randul exista.
 */
export function changesOf(row: Record<string, unknown>, environments: Pair[]): [string, string, string][] {
  const env = (value: unknown) => environments.find((e) => e.value === Number(value))?.label ?? String(value ?? 0)
  const out: [string, string, string][] = []

  if (String(row.old_name ?? '') !== String(row.new_name ?? '')) {
    out.push(['name', String(row.old_name ?? ''), String(row.new_name ?? '')])
  }
  if (Number(row.old_status) !== Number(row.new_status)) {
    out.push(['state', String(row.old_status ?? ''), String(row.new_status ?? '')])
  }
  if (String(row.old_criticality ?? '') !== String(row.new_criticality ?? '')) {
    out.push([
      'criticality',
      row.old_criticality === null || row.old_criticality === undefined ? '—' : String(row.old_criticality),
      row.new_criticality === null || row.new_criticality === undefined ? '—' : String(row.new_criticality),
    ])
  }
  if (Number(row.old_environment ?? 0) !== Number(row.new_environment ?? 0)) {
    out.push(['environment', env(row.old_environment), env(row.new_environment)])
  }
  if (String(row.old_owner ?? '') !== String(row.new_owner ?? '')) {
    out.push(['business owner', person(row.old_owner), person(row.new_owner)])
  }
  if (String(row.old_custodian ?? '') !== String(row.new_custodian ?? '')) {
    out.push(['technical custodian', person(row.old_custodian), person(row.new_custodian)])
  }

  return out
}

/**
 * Cine raspundea, si cine raspunde.
 *
 * Zero e "nimeni", nu omul cu numarul zero. In PHP `?:` inghitea si zero-ul, si
 * NULL-ul; in TypeScript `String(0)` e "0", care e adevarat - deci "nobody"
 * n-ar fi aparut niciodata pe un rand unde chiar asta se schimbase.
 */
function person(value: unknown): string {
  const id = Number(value ?? 0)
  return Number.isFinite(id) && id > 0 ? String(id) : 'nobody'
}

function Card({
  title,
  tone,
  icon,
  note,
  count,
  children,
}: {
  title: string
  tone: string
  icon: string
  note: string
  count: number
  children: ReactNode
}) {
  return (
    <div className="adm-card" style={{ marginBottom: 16, borderLeft: `3px solid ${tone}` }}>
      <div className="adm-card-head">
        <h5>
          <i className={`fas ${icon}`}></i> {title} <span className="badge">{count}</span>
        </h5>
        <span className="adm-card-note">{note}</span>
      </div>
      <table className="table adm-list">
        <tbody>{children}</tbody>
      </table>
    </div>
  )
}

function Items({
  rows,
  title,
  tone,
  icon,
  note,
}: {
  rows: Record<string, unknown>[]
  title: string
  tone: string
  icon: string
  note: string
}) {
  if (rows.length === 0) return null
  return (
    <Card title={title} tone={tone} icon={icon} note={note} count={rows.length}>
      {rows.map((row, index) => (
        <tr key={index}>
          <td>
            <a href={`/app/cmdb/view/${Number(row.ci_id)}`} target="_blank" rel="noopener">
              {String(row.name ?? '')}
            </a>{' '}
            {/* Numai productia se marcheaza: deosebirea care schimba o hotarare
                e productie sau nu. */}
            {Number(row.environment ?? 0) === 1 && <span className="adm-pill adm-pill-warn">production</span>}
          </td>
          <td style={{ width: 120 }} className="text-right">
            {row.criticality !== null && row.criticality !== undefined && (
              <span className="adm-muted">criticality {Number(row.criticality)}</span>
            )}
          </td>
        </tr>
      ))}
    </Card>
  )
}

function Edges({
  rows,
  title,
  tone,
  icon,
  note,
}: {
  rows: Record<string, unknown>[]
  title: string
  tone: string
  icon: string
  note: string
}) {
  if (rows.length === 0) return null
  return (
    <Card title={title} tone={tone} icon={icon} note={note} count={rows.length}>
      {rows.map((row, index) => (
        <tr key={index}>
          <td>
            <a href={`/app/cmdb/view/${Number(row.source_ci_id)}`} target="_blank" rel="noopener">
              {String(row.source_name ?? '')}
            </a>{' '}
            <i className="fas fa-arrow-right adm-muted"></i>{' '}
            <a href={`/app/cmdb/view/${Number(row.target_ci_id)}`} target="_blank" rel="noopener">
              {String(row.target_name ?? '')}
            </a>
          </td>
        </tr>
      ))}
    </Card>
  )
}
